import React, { useEffect, useState, useRef } from 'react'
import { Grid, Typography, Paper, CircularProgress, Chip } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { ReactComponent as ConfirmationIcon } from '../../assets/signup/confirmation.svg'
import { windowHeight, windowWidth } from '../../utils/utils'
import { backendClient } from '../../api/backend'
import { loginSuccess } from '../../redux/actions/authAction'
import { fetchSubscription } from '../../redux/actions/subscriptionAction'
import { tracker } from '../../analytics'

const MAX_POLLS = 20
const POLL_INTERVAL_MS = 2000

const PaymentSuccessScreen = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const theme = useTheme()
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  const sessionId = searchParams.get('session_id')

  const [status, setStatus] = useState('pending')
  const [planInfo, setPlanInfo] = useState(null)
  const pollCount = useRef(0)

  useEffect(() => {
    if (!sessionId) {
      setStatus('active')
      scheduleRedirect()
      return
    }

    const poll = async () => {
      try {
        const data = await backendClient.getStripeSessionStatus(sessionId)
        if (data.status && data.status !== 'pending') {
          setStatus(data.status)
          setPlanInfo(data)
          trackPurchase(data)
          tracker.conversion({ category: 'checkout_success', action: 'payment_completed', properties: { plan: data.plan, billing_cycle: data.billing_cycle, session_id: sessionId } })
          scheduleRedirect()
          return
        }
      } catch (_) {
        // Silently retry
      }

      pollCount.current += 1
      if (pollCount.current >= MAX_POLLS) {
        setStatus('active')
        scheduleRedirect()
        return
      }

      setTimeout(poll, POLL_INTERVAL_MS)
    }

    setTimeout(poll, 1000)
  }, [sessionId]) // eslint-disable-line

  const scheduleRedirect = () => {
    localStorage.removeItem('pendingCheckout')
    const pendingLoginDataRaw = sessionStorage.getItem('pendingLoginData')
    if (pendingLoginDataRaw) {
      try {
        const loginData = JSON.parse(pendingLoginDataRaw)
        sessionStorage.removeItem('pendingLoginData')
        loginData.user.is_allowed = true
        dispatch(loginSuccess(loginData))
        setTimeout(() => navigate('/'), 3000)
        return
      } catch (_) {}
    }
    // Already-logged-in user: refresh subscription data so SubscriptionRoute sees it
    const token = localStorage.getItem('access_token')
    if (token) {
      dispatch(fetchSubscription())
      setTimeout(() => navigate('/'), 3000)
      return
    }
    setTimeout(() => navigate('/login'), 3000)
  }

  const trackPurchase = (data) => {
    try {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'purchase_success',
        ecommerce: {
          transaction_id: sessionId,
          currency: 'USD',
          items: [{
            item_name: data.plan || 'subscription',
            item_category: 'Subscription',
            quantity: 1,
          }],
        },
      })
    } catch (_) {}
  }

  const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : ''
  const isConfirmed = ['active', 'trial'].includes(status)
  const isDark = theme.palette.mode === 'dark'

  return (
    <Grid
      container
      sx={{
        height: '90vh',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Paper
        elevation={isDark ? 0 : 4}
        sx={{
          borderRadius: '20px',
          width: '60%',
          padding: '40px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '60vh',
          backgroundColor: theme.palette.background.paper,
          border: isDark ? `1px solid ${theme.palette.divider}` : 'none',
        }}
      >
        {!isConfirmed && status === 'pending' ? (
          <CircularProgress
            size={80}
            sx={{ color: theme.palette.primary.main, mb: '20px' }}
          />
        ) : (
          <ConfirmationIcon
            style={{
              width: windowHeight / 2.5,
              height: windowHeight / 2.5,
              marginRight: windowWidth / 40,
              marginBottom: '20px',
            }}
          />
        )}

        <Typography variant="h4" sx={{ mb: 2, color: theme.palette.text.primary }}>
          {!isConfirmed && status === 'pending'
            ? t('payment_confirming_subscription')
            : status === 'trial'
            ? t('payment_trial_started')
            : t('payment_successful')}
        </Typography>

        <Typography variant="body1" sx={{ mb: 2, color: theme.palette.text.secondary }}>
          {status === 'pending'
            ? t('payment_please_wait')
            : status === 'trial'
            ? t('payment_trial_active')
            : t('payment_subscription_active')}
        </Typography>

        {planInfo?.plan && (
          <Chip
            label={t('payment_plan_label', {
              plan: capitalize(planInfo.plan),
              cycle: capitalize(planInfo.billing_cycle || 'monthly'),
            })}
            sx={{
              backgroundColor: isDark ? theme.palette.primary.main : '#1c1917',
              color: isDark ? '#000000' : '#c9a96e',
              fontWeight: 700,
              mb: 2,
            }}
          />
        )}

        {planInfo?.trial_end && (
          <Typography variant="body2" sx={{ mb: 2, color: theme.palette.text.secondary }}>
            {t('payment_trial_ends_on', {
              date: new Date(planInfo.trial_end).toLocaleDateString(i18n.language, {
                year: 'numeric', month: 'long', day: 'numeric',
              }),
            })}
          </Typography>
        )}

        {isConfirmed && (
          <Typography variant="body2" sx={{ mb: 4, color: theme.palette.text.disabled }}>
            {t('payment_redirecting')}
          </Typography>
        )}
      </Paper>
    </Grid>
  )
}

export default PaymentSuccessScreen
